/**
 * TMX (Tiled Map Editor) Parser
 *
 * Parses TMX XML format into TypeScript data structures.
 * Handles chunked infinite maps, tile flipping, and animations.
 */

import type {
  TMXMap,
  TMXTileset,
  TMXLayer,
  TMXChunk,
  TMXAnimation,
  TMXTileData,
} from "@/types/tmx.types"
import { TMX_FLIP_H, TMX_FLIP_V, TMX_FLIP_D, TMX_GID_MASK } from "@/types/tmx.types"

/**
 * Parse a CSV string of tile GIDs into a number array
 */
export function parseChunkCSV(csv: string): number[] {
  return csv
    .trim()
    .split(/[,\n]+/)
    .map((s) => {
      const trimmed = s.trim()
      if (!trimmed) return 0
      // Parse as unsigned 32-bit integer to handle flip flags
      return Number(trimmed) >>> 0
    })
}

/**
 * Parse animation frames from tileset XML
 */
function parseAnimations(tilesetElement: Element): Record<number, TMXAnimation> {
  const animations: Record<number, TMXAnimation> = {}

  const tileElements = tilesetElement.querySelectorAll("tile")
  for (const tile of tileElements) {
    const tileId = parseInt(tile.getAttribute("id") || "0", 10)
    const animationEl = tile.querySelector("animation")

    if (animationEl) {
      const frameElements = animationEl.querySelectorAll("frame")
      const frames: TMXAnimation["frames"] = []
      let totalDuration = 0

      for (const frame of frameElements) {
        const duration = parseInt(frame.getAttribute("duration") || "150", 10)
        frames.push({
          tileId: parseInt(frame.getAttribute("tileid") || "0", 10),
          duration,
        })
        totalDuration += duration
      }

      if (frames.length > 0) {
        animations[tileId] = { frames, totalDuration }
      }
    }
  }

  return animations
}

/**
 * Parse a tileset element from TMX
 */
function parseTileset(element: Element): TMXTileset {
  const imageEl = element.querySelector("image")

  return {
    firstgid: parseInt(element.getAttribute("firstgid") || "1", 10),
    name: element.getAttribute("name") || "",
    tileWidth: parseInt(element.getAttribute("tilewidth") || "16", 10),
    tileHeight: parseInt(element.getAttribute("tileheight") || "16", 10),
    tileCount: parseInt(element.getAttribute("tilecount") || "0", 10),
    columns: parseInt(element.getAttribute("columns") || "1", 10),
    image: imageEl?.getAttribute("source") || "",
    imageWidth: parseInt(imageEl?.getAttribute("width") || "0", 10),
    imageHeight: parseInt(imageEl?.getAttribute("height") || "0", 10),
    animations: parseAnimations(element),
  }
}

/**
 * Parse a chunk element from TMX layer
 */
function parseChunk(element: Element): TMXChunk {
  const csvData = element.textContent || ""
  return {
    x: parseInt(element.getAttribute("x") || "0", 10),
    y: parseInt(element.getAttribute("y") || "0", 10),
    width: parseInt(element.getAttribute("width") || "16", 10),
    height: parseInt(element.getAttribute("height") || "16", 10),
    data: parseChunkCSV(csvData),
  }
}

/**
 * Parse a layer element from TMX
 */
function parseLayer(element: Element): TMXLayer {
  const chunks: TMXChunk[] = []
  const dataEl = element.querySelector("data")

  if (dataEl) {
    const chunkElements = dataEl.querySelectorAll("chunk")
    for (const chunk of chunkElements) {
      chunks.push(parseChunk(chunk))
    }
  }

  return {
    id: parseInt(element.getAttribute("id") || "0", 10),
    name: element.getAttribute("name") || "",
    offsetX: parseFloat(element.getAttribute("offsetx") || "0"),
    offsetY: parseFloat(element.getAttribute("offsety") || "0"),
    visible: element.getAttribute("visible") !== "0",
    opacity: parseFloat(element.getAttribute("opacity") || "1"),
    chunks,
  }
}

/**
 * Parse TMX XML string into a TMXMap structure
 */
export function parseTMX(xmlString: string): TMXMap {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, "text/xml")
  const mapEl = doc.querySelector("map")

  if (!mapEl) {
    throw new Error("Invalid TMX: no <map> element found")
  }

  // Parse tilesets (sorted by firstgid)
  const tilesetElements = mapEl.querySelectorAll(":scope > tileset")
  const tilesets: TMXTileset[] = []
  for (const el of tilesetElements) {
    tilesets.push(parseTileset(el))
  }
  tilesets.sort((a, b) => a.firstgid - b.firstgid)

  // Parse layers (maintain order)
  const layerElements = mapEl.querySelectorAll(":scope > layer")
  const layers: TMXLayer[] = []
  for (const el of layerElements) {
    layers.push(parseLayer(el))
  }

  return {
    version: mapEl.getAttribute("version") || "1.0",
    orientation: (mapEl.getAttribute("orientation") || "orthogonal") as TMXMap["orientation"],
    renderOrder: (mapEl.getAttribute("renderorder") || "right-down") as TMXMap["renderOrder"],
    width: parseInt(mapEl.getAttribute("width") || "0", 10),
    height: parseInt(mapEl.getAttribute("height") || "0", 10),
    tileWidth: parseInt(mapEl.getAttribute("tilewidth") || "16", 10),
    tileHeight: parseInt(mapEl.getAttribute("tileheight") || "16", 10),
    infinite: mapEl.getAttribute("infinite") === "1",
    tilesets,
    layers,
  }
}

/**
 * Parse an external tileset (.tsx) file and merge animations
 */
export function parseExternalTileset(xmlString: string, firstgid: number): TMXTileset {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, "text/xml")
  const tilesetEl = doc.querySelector("tileset")

  if (!tilesetEl) {
    throw new Error("Invalid TSX: no <tileset> element found")
  }

  const imageEl = tilesetEl.querySelector("image")

  return {
    firstgid,
    name: tilesetEl.getAttribute("name") || "",
    tileWidth: parseInt(tilesetEl.getAttribute("tilewidth") || "16", 10),
    tileHeight: parseInt(tilesetEl.getAttribute("tileheight") || "16", 10),
    tileCount: parseInt(tilesetEl.getAttribute("tilecount") || "0", 10),
    columns: parseInt(tilesetEl.getAttribute("columns") || "1", 10),
    image: imageEl?.getAttribute("source") || "",
    imageWidth: parseInt(imageEl?.getAttribute("width") || "0", 10),
    imageHeight: parseInt(imageEl?.getAttribute("height") || "0", 10),
    animations: parseAnimations(tilesetEl),
  }
}

/**
 * Find the tileset that contains a given GID
 */
export function findTilesetForGID(gid: number, tilesets: TMXTileset[]): TMXTileset | null {
  // Tilesets are sorted by firstgid, find the last one where firstgid <= gid
  let result: TMXTileset | null = null
  for (const ts of tilesets) {
    if (gid >= ts.firstgid) {
      result = ts
    } else {
      break
    }
  }
  return result
}

/**
 * Extract tile data from a raw GID, including flip flags and source position
 */
export function getTileData(rawGid: number, tilesets: TMXTileset[]): TMXTileData | null {
  // Extract flip flags from upper bits
  const flipH = (rawGid & TMX_FLIP_H) !== 0
  const flipV = (rawGid & TMX_FLIP_V) !== 0
  const flipD = (rawGid & TMX_FLIP_D) !== 0

  // Mask off flip bits to get actual GID
  const gid = rawGid & TMX_GID_MASK

  // GID 0 means empty tile
  if (gid === 0) return null

  // Find tileset
  const tileset = findTilesetForGID(gid, tilesets)
  if (!tileset) return null

  // Calculate local tile ID and source position
  const localId = gid - tileset.firstgid
  const srcX = (localId % tileset.columns) * tileset.tileWidth
  const srcY = Math.floor(localId / tileset.columns) * tileset.tileHeight

  return {
    tileset,
    localId,
    srcX,
    srcY,
    flipH,
    flipV,
    flipD,
  }
}

/**
 * Get the current animation frame for a tile based on game time
 */
export function getAnimatedTileId(
  tileset: TMXTileset,
  localId: number,
  gameTimeMs: number,
): number {
  const animation = tileset.animations[localId]
  if (!animation || animation.frames.length === 0) {
    return localId // No animation, return original
  }

  // Calculate which frame we're on based on time
  const cycleTime = gameTimeMs % animation.totalDuration
  let elapsed = 0

  for (const frame of animation.frames) {
    elapsed += frame.duration
    if (cycleTime < elapsed) {
      return frame.tileId
    }
  }

  // Fallback to last frame
  return animation.frames[animation.frames.length - 1].tileId
}

/**
 * Get source coordinates for a tile, handling animations
 */
export function getAnimatedSrcCoords(
  tileset: TMXTileset,
  localId: number,
  gameTimeMs: number,
): { srcX: number; srcY: number } {
  const animatedId = getAnimatedTileId(tileset, localId, gameTimeMs)
  return {
    srcX: (animatedId % tileset.columns) * tileset.tileWidth,
    srcY: Math.floor(animatedId / tileset.columns) * tileset.tileHeight,
  }
}
